import React, { Fragment } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  BellIcon,
  XMarkIcon,
  ChevronRightIcon,
  ArrowRightOnRectangleIcon,
  ClipboardDocumentListIcon,
  DevicePhoneMobileIcon,
  BuildingOffice2Icon,
  WrenchScrewdriverIcon,
  ClockIcon,
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon,
  //codex-- Use the existing outline icon set for the team feed destination.
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import logo from '../assets/images/logo.png';
import { NavLink, useNavigate } from 'react-router-dom';
import AvatarLocation from '../components/AvatarLocation';
import { useUserData } from '../context/UserData';
import axios from 'axios';

function classNames(...xs) {
  return xs.filter(Boolean).join(' ');
}

const TechNavBar = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const userData = useUserData();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      const { data } = await axios.post(
        `${apiUrl}/users_login.php`,
        { status: 'signout', data: {} },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (data.success) navigate('/login');
    } catch (e) {
      console.error('Signout error', e);
    }
  };

  // Navigation (valid outline icons only)
  const nav = [
    //codex-- Make the independent team feed reachable from the existing mobile menu.
    { to: '/t/tech_wall', label: 'Tech Wall', icon: ChatBubbleLeftRightIcon },
    { to: 'tnewsupdate', label: 'News • Documents', icon: ClipboardDocumentListIcon },
    { to: '#', label: 'Request Access Code', icon: DevicePhoneMobileIcon },
    { to: '/t/tkfdny', label: 'FDNY Inspections', icon: BuildingOffice2Icon },
    { to: 'twip', label: 'WIP List', icon: WrenchScrewdriverIcon },
    { to: 'twkt', label: 'Timesheets', icon: ClockIcon },
    { to: '/t/history', label: 'Schedule History', icon: ClipboardDocumentListIcon },    
    { to: 'tech_schedule', label: 'Tech Schedule (All)', icon: CalendarDaysIcon },
    { to: '/t/tinspection', label: 'Maintenance Inspections Queue', icon: ClipboardDocumentCheckIcon },
    { to: 'tservlist', label: 'Service Calls Queue', icon: WrenchScrewdriverIcon },
    { to: 'twiplist', label: 'WIP Queue', icon: ClipboardDocumentCheckIcon },
    { to: '/t/tcredit_hold', label: 'Credit Hold List', icon: ClipboardDocumentListIcon },
  // { to: 'tservhistory/list', label: 'Service Calls History', icon: ClipboardDocumentListIcon },
    { to: '/t/tvacationreq', label: 'Vacation Request', icon: CalendarDaysIcon },
    { to: '/t/tchkinout', label: 'Checking', icon: ClockIcon },
    { to: '/t/tovetimeavailable', label: 'Overtime Availability', icon: ClockIcon },
    { to: '/t/t_op_list', label: 'Operation Queue', icon: WrenchScrewdriverIcon },
  ];

  const title = userData?.users?.title?.toLowerCase();
  const dept  = userData?.users?.department?.toLowerCase();

  if (['service manager','installation manager', 'supervisor'].includes(title) || dept === 'admin') {
    nav.push({ to: 'full_viewer_mobile', label: 'Full Schedule', icon: CalendarDaysIcon });
  }

  return (
    <Disclosure as="nav" className="sticky top-0 z-50 bg-black/80 backdrop-blur-md shadow-sm ring-1 ring-gray-200">
      {({ open }) => (
        <>
          {/* Top bar */}
          <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-8">
            <div className="flex h-14 items-center justify-between">
              <div className="flex items-center gap-2">
                <img className="h-8 w-auto" src={logo} alt="IRL-Mainframe" />
                <span className="hidden sm:inline text-sm font-semibold text-white">Tech</span>
              </div>

              {/* Desktop right side */}
              <div className="hidden sm:flex items-center gap-3">
                {/*codex-- Keep the wall reachable when the mobile menu is hidden on wider screens. */}
                <NavLink to="/t/tech_wall" className="inline-flex min-h-[44px] items-center gap-2 rounded-lg px-3 text-sm font-semibold text-gray-200 hover:bg-white/10 hover:text-white">
                  <ChatBubbleLeftRightIcon aria-hidden="true" className="h-5 w-5" />Tech Wall
                </NavLink>
                <button
                  type="button"
                  className="rounded-full p-1 text-gray-200 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <span className="sr-only">Notifications</span>
                  <BellIcon className="h-6 w-6" />
                </button>

                <Menu as="div" className="relative">
                  <Menu.Button className="flex items-center rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <AvatarLocation className="h-9 w-9 rounded-full" avatar={userData?.avatar || 'no_pic.png'} />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-20 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black/5 focus:outline-none overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">
                          {userData?.users?.first_name} {userData?.users?.last_name}
                        </p>
                        <p className="text-xs text-gray-500">{userData?.users?.email}</p>
                      </div>
                      <Menu.Item>
                        {({ active }) => (
                          <NavLink
                            to={`/t/tp/${userData?.users?.id}`}
                            className={classNames('block px-4 py-2.5 text-sm', active ? 'bg-gray-50 text-gray-900' : 'text-gray-700')}
                          >
                            Profile
                          </NavLink>
                        )}
                      </Menu.Item>
                      {userData?.users?.department === 'Admin' && (
                        <Menu.Item>
                          {({ active }) => (
                            <NavLink
                              to="/"
                              className={classNames('block px-4 py-2.5 text-sm', active ? 'bg-gray-50 text-gray-900' : 'text-gray-700')}
                            >
                              Office Mode
                            </NavLink>
                          )}
                        </Menu.Item>
                      )}
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleSignOut}
                            className={classNames(
                              'w-full text-left px-4 py-2.5 text-sm flex items-center gap-2',
                              active ? 'bg-gray-50 text-rose-700' : 'text-rose-600'
                            )}
                          >
                            <ArrowRightOnRectangleIcon className="h-5 w-5" />
                            Sign out
                          </button>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>

              {/* Mobile menu button */}
              <div className="sm:hidden">
                <Disclosure.Button className="inline-flex items-center justify-center rounded-lg p-2 text-gray-100 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <span className="sr-only">Open main menu</span>
                  {open ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          {/* Mobile panel: full screen sheet */}
          <Disclosure.Panel className="sm:hidden">
            {({ close }) => (
              <>
                {/* overlay */}
                <div className="fixed inset-0 bg-black/40 backdrop-blur-xs" onClick={() => close()} />

                <div className="fixed inset-x-0 top-0 mt-14 h-[calc(100vh-56px)] overflow-y-auto rounded-t-2xl bg-white shadow-2xl ring-1 ring-gray-200">
                  {/* user header */}
                  <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                    <div onClick={() => {   
                        navigate(`/t/tp/${userData?.users?.id}`)
                        close();
                      }} className="flex items-center gap-3 cursor-pointer">
                      <AvatarLocation className="h-10 w-10 rounded-full" avatar={userData?.avatar || 'no_pic.png'} />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {userData?.users?.first_name} {userData?.users?.last_name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">{userData?.users?.email}</div>
                        <p className='text-xs text-gray-400'>Profile</p>                        
                      </div>
                    </div>
                    <button
                      type="button"
                      className="ml-auto rounded-full p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <BellIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* nav list */}
                  <div className="px-3 py-3 space-y-1">
                    {nav.map(({ to, label, icon: Icon }) => (
                      <NavLink
                        key={label}
                        to={to}
                        onClick={() => close()} // close sheet on navigate
                        className={({ isActive }) =>
                          classNames(
                            'flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium shadow-sm ring-1 transition-colors',
                            isActive
                              ? 'bg-indigo-50 text-indigo-700 ring-indigo-200'
                              : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'
                          )
                        }
                      >
                        <Icon className="h-5 w-5 text-gray-400" />
                        <span className="flex-1 truncate">{label}</span>
                        <ChevronRightIcon className="h-5 w-5 text-gray-300" />
                      </NavLink>
                    ))}
                  </div>

                  {/* footer actions */}
                  <div className="px-4 py-4 border-t border-gray-100 flex items-center justify-between">
                    {userData?.users?.o_f_type === 'Office' ? (
                      <NavLink
                        to="/"
                        onClick={() => close()}
                        className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50"
                      >
                        Office Mode
                      </NavLink>
                    ) : (
                      <span />
                    )}

                    <button
                      onClick={() => {
                        close();
                        handleSignOut();
                      }}
                      className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500"
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5" />
                      Sign out
                    </button>
                  </div>

                  {/* safe area padding */}
                  <div className="h-4" />
                </div>
              </>
            )}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

export default TechNavBar;
